import { Box, Container, Grid, Theme, Typography, useMediaQuery } from '@material-ui/core';
import { createStyles, makeStyles } from '@material-ui/styles';
import React, { FC, useRef, useState } from 'react';
import Carousel from 'react-multi-carousel';
import 'react-multi-carousel/lib/styles.css';
import { SliderArrows } from 'components/structures/careers';
import SliderCard, { SliderCardProps } from 'components/structures/careers/SliderCard';

interface SliderSectionProps {
  sliderList: Array<SliderCardProps>;
}

const useStyles = makeStyles((theme: Theme) => {
  return createStyles({
    root: {
      marginBottom: theme.spacing(15),
    },
    container: {
      padding: theme.spacing(7, 0, 4),
      [theme.breakpoints.up('sm')]: {
        padding: theme.spacing(15, 0, 11),
      },
    },
    title: {
      fontWeight: 500,
      color: theme.palette.primary.dark,
      maxWidth: 220,
      [theme.breakpoints.up('sm')]: {
        maxWidth: 300,
      },
      [theme.breakpoints.up('md')]: {
        maxWidth: '100%',
      },
    },
    orangeTitle: {
      color: theme.palette.primary.main,
    },
    boxCarousel: {
      position: 'relative',
      marginLeft: 1,
      [theme.breakpoints.up('md')]: {
        marginLeft: theme.spacing(-2),
      },
      '& div': {
        '& ul': {
          transition: 'all 2s ease 0s !important',
        },
      },
    },
    boxWrapper: {
      paddingLeft: theme.spacing(3),
      [theme.breakpoints.up('md')]: {
        paddingLeft: theme.spacing(5),
      },
    },
  });
});

const SliderSection: FC<SliderSectionProps> = ({ sliderList }) => {
  const ref = useRef(null);
  const [slide, setSlide] = useState(0);
  const [loading, setLoading] = useState(false);
  const [hover, setHover] = useState<boolean>(false);
  const classes = useStyles();
  const isSmall = useMediaQuery((theme: Theme) => theme.breakpoints.up('sm'));

  const prev = () => {
    if (!loading) {
      ref.current.previous();
      if (slide === 0) {
        setSlide(sliderList.length - 1);
      } else {
        setSlide(slide - 1);
      }
    }
  };

  const next = () => {
    if (!loading) {
      ref.current.next();
      if (slide >= sliderList.length - 1) {
        setSlide(0);
      } else {
        setSlide(slide + 1);
      }
    }
  };

  return (
    <>
      <Container fixed className={classes.root} data-aos="fade-up" id={'whyUtilitiesOne'}>
        {!!sliderList.length && (
          <Grid container>
            <Grid item xs={12} onMouseOver={() => setHover(true)} onMouseOut={() => setHover(false)}>
              <Grid direction="row" justify="space-between" container className={classes.container}>
                <Grid item>
                  <Typography variant={isSmall ? 'h2' : 'h4'} className={classes.title}>
                    Why Work at <span className={classes.orangeTitle}>Utilities One</span>
                  </Typography>
                </Grid>
                <Grid item>
                  {sliderList.length > 1 && (
                    <SliderArrows totalSlides={Array.from(sliderList.keys())} currentSlide={slide} next={next} prev={prev} />
                  )}
                </Grid>
              </Grid>
              <Box ml={isSmall ? 0 : 2.5} className={classes.boxCarousel}>
                <Carousel
                  ref={ref}
                  responsive={{
                    mobile: {
                      breakpoint: {
                        max: 600,
                        min: 0,
                      },
                      items: 1.02,
                    },
                    tablet: {
                      breakpoint: {
                        max: 1024,
                        min: 600,
                      },
                      items: 2.03,
                    },
                    desktop: {
                      breakpoint: {
                        max: 4000,
                        min: 1025,
                      },
                      items: 4,
                    },
                  }}
                  beforeChange={() => setLoading(true)}
                  afterChange={() => setLoading(false)}
                  additionalTransfrom={0}
                  minimumTouchDrag={50}
                  arrows={false}
                  swipeable={true}
                  ssr={true}
                  draggable={false}
                  keyBoardControl={false}
                  slidesToSlide={1}
                  infinite={true}
                  itemClass={classes.boxWrapper}
                  autoPlay={!hover}
                  autoPlaySpeed={3000}
                  pauseOnHover
                >
                  {sliderList.map(({ title }, index) => (
                    <Box key={index}>
                      <SliderCard title={title} index={index + 1} />
                    </Box>
                  ))}
                </Carousel>
              </Box>
            </Grid>
          </Grid>
        )}
      </Container>
    </>
  );
};

export default SliderSection;
